import { t, localeTag } from './i18n.js';
import { useEffect, useState } from 'react';
import { api, send } from './api.js';
import { Notice, formatStorageBytes } from './components.js';
const money = (n: any) => `${Number(n).toLocaleString(localeTag())} FCFA`;
const date = (s: string) => new Date(s).toLocaleDateString(localeTag(), { timeZone: 'UTC' });
const stageNames: Record<string, string> = {
  unmanaged: 'Facturation non activée',
  active: 'À jour',
  payment_due: 'Règlement attendu',
  sending_paused: 'Émission suspendue',
  export_only: 'Consultation et export',
  retention_pending: 'Fin de conservation à traiter',
};
const methodNames: Record<string, string> = {
  mobile_money: 'Mobile Money',
  card: 'Carte',
  transfer: 'Virement',
};
/** Billing of an organisation ordered on the Vename website: read-only mirror of its invoices. */
function SiteBilling({ data, error }: { data: any; error: string }) {
  const site = data.external;
  const now = new Date();
  // The server keeps the trial until a scheduled cancellation takes effect.
  const inTrial =
    data.access?.trialActive ?? (site.trialEndsAt && now < new Date(site.trialEndsAt));
  return (
    <section className="billing-panel">
      <h3>{t('Facturation de l’organisation')}</h3>
      {error && <Notice type="error">{error}</Notice>}
      <p>
        <strong>{inTrial ? t('Essai en cours') : t(stageNames[data.access.stage])}</strong>
      </p>
      <p>
        {t(
          'La facturation de cette organisation est tenue par le site Vename : factures, paiements et relances y sont gérés. Vename Mail en reçoit une copie pour appliquer l’état du service.',
        )}
      </p>
      {inTrial && <p>{t('Essai gratuit jusqu’au {date}.', { date: date(site.trialEndsAt) })}</p>}
      {!site.trialStartedAt && !site.cancelEffectiveAt && (
        <p>{t('L’essai démarrera dès que le domaine sera vérifié.')}</p>
      )}
      {site.cancelEffectiveAt && (
        <p>{t('Résiliation effective le {date}.', { date: date(site.cancelEffectiveAt) })}</p>
      )}
      {site.invoices.length === 0 ? (
        <p>{t('Aucune facture reçue du site pour l’instant.')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('Facture')}</th>
                <th>{t('Période')}</th>
                <th>{t('Échéance')}</th>
                <th>{t('Total TTC')}</th>
                <th>{t('Reste dû')}</th>
              </tr>
            </thead>
            <tbody>
              {site.invoices.map((i: any) => (
                <tr key={i.id}>
                  <td>{i.invoiceRef}</td>
                  <td>
                    {date(i.periodStart)} – {date(i.periodEnd)}
                  </td>
                  <td>{date(i.dueAt)}</td>
                  <td>{money(i.total)}</td>
                  <td>
                    {i.voidedAt ? t('Annulée') : i.remaining > 0 ? money(i.remaining) : t('Réglée')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {site.payments.length > 0 && (
        <details>
          <summary>{t('Paiements reçus du site')}</summary>
          <ul>
            {site.payments.map((p: any) => (
              <li key={p.id}>
                {date(p.paidAt)} · {money(p.amount)} · {t(methodNames[p.method] ?? p.method)} ·{' '}
                {p.invoiceRef}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
function download(value: any, name: string) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function Billing({
  organizationId,
  subscription,
  catalog,
  onChange,
}: {
  organizationId?: string;
  subscription: any;
  catalog: any;
  onChange: () => Promise<void>;
}) {
  const path = organizationId
    ? `/platform/organizations/${organizationId}/billing`
    : '/admin/billing';
  const [data, setData] = useState<any>(),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [selected, setSelected] = useState<any>();
  const [seller, setSeller] = useState(''),
    [customer, setCustomer] = useState(''),
    [tax, setTax] = useState(0);
  const [payment, setPayment] = useState<any>(),
    [reference, setReference] = useState(''),
    [method, setMethod] = useState('transfer');
  const [schedule, setSchedule] = useState<any>();
  const load = async () => setData(await api(path));
  useEffect(() => {
    void load().catch((e) => setError(e.message));
  }, [path, subscription]);
  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
      await onChange();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  if (!data)
    return (
      <section className="billing-panel">
        <h3>{t('Facturation')}</h3>
        {error && <Notice type="error">{error}</Notice>}
      </section>
    );
  if (data.external) return <SiteBilling data={data} error={error} />;
  return (
    <section className="billing-panel">
      <h3>{t('Facturation de l’organisation')}</h3>
      {error && <Notice type="error">{error}</Notice>}
      <p>
        <strong>{t(stageNames[data.access.stage])}</strong>
      </p>
      {!data.account ? (
        <>
          <p>{t('Les organisations pilotes ne sont pas facturées automatiquement.')}</p>
          {organizationId && subscription && (
            <details>
              <summary>{t('Activer la facturation à partir d’aujourd’hui')}</summary>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void act(async () => {
                    await send(path + '/activate', {
                      expectedSubscriptionRevision: subscription.revision,
                      profile: {
                        seller,
                        customer,
                        taxBasisPoints: Math.round(tax * 100),
                        currency: 'XOF',
                      },
                    });
                  });
                }}
              >
                <label>
                  {t('Émetteur : raison sociale, adresse, identifiants fiscaux')}
                  <textarea
                    required
                    minLength={3}
                    maxLength={2000}
                    value={seller}
                    onChange={(e) => setSeller(e.target.value)}
                  />
                </label>
                <label>
                  {t('Client : raison sociale, adresse, identifiants de facturation')}
                  <textarea
                    required
                    minLength={3}
                    maxLength={2000}
                    value={customer}
                    onChange={(e) => setCustomer(e.target.value)}
                  />
                </label>
                <label>
                  {t('Taux de taxe validé (%)')}
                  <input
                    required
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={tax}
                    onChange={(e) => setTax(Number(e.target.value))}
                  />
                </label>
                <p>
                  {t(
                    'La première facture couvrira {units} unités, pour un {cadence}. Aucun paiement ne sera déclenché.',
                    {
                      units: subscription.units,
                      cadence: subscription.cadence === 'yearly' ? t('an') : t('mois'),
                    },
                  )}
                </p>
                <button className="primary" disabled={busy}>
                  {t('Activer et émettre la première facture')}
                </button>
              </form>
            </details>
          )}
        </>
      ) : (
        <>
          <p>
            {t('Période : {start} au {end} (fin exclusive, UTC).', {
              start: date(data.account.period_start),
              end: date(data.account.period_end),
            })}
          </p>
          {data.account.cancel_at_end && (
            <Notice>
              {t(
                'Résiliation prévue au {date}. Ensuite : consultation et export pendant 30 jours.',
                { date: date(data.account.period_end) },
              )}
            </Notice>
          )}
          {data.account.scheduled && (
            <Notice>
              {t(
                'Changement prévu à l’échéance : {units} unités, formule {plan}, cycle {cadence}.',
                {
                  units: data.account.scheduled.units,
                  plan: data.account.scheduled.plan,
                  cadence: data.account.scheduled.cadence === 'yearly' ? t('annuel') : t('mensuel'),
                },
              )}
            </Notice>
          )}
          {!data.account.ended_at && (
            <details>
              <summary>{t('Gérer le prochain renouvellement')}</summary>
              <p>
                {t(
                  'Les retraits prennent effet à l’échéance, sans remboursement automatique. Les plafonds de stockage et les unités attribuées doivent rester compatibles.',
                )}
              </p>
              <button
                onClick={() =>
                  setSchedule({
                    units: subscription.units,
                    plan: subscription.plan,
                    cadence: subscription.cadence,
                    storage25: subscription.extensions.storage25 ?? 0,
                    storage100: subscription.extensions.storage100 ?? 0,
                  })
                }
              >
                {t('Préparer un changement')}
              </button>
              {schedule && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void act(async () => {
                      await send(path + '/schedule', {
                        revision: data.account.revision,
                        cancel: false,
                        subscription: {
                          ...schedule,
                          catalogVersion: subscription.catalog_version,
                          expectedRevision: subscription.revision,
                          reference: t('Changement demandé à échéance'),
                        },
                      });
                      setSchedule(undefined);
                    });
                  }}
                >
                  <label>
                    {t('Unités au renouvellement')}
                    <input
                      required
                      min={1}
                      max={500}
                      type="number"
                      value={schedule.units}
                      onChange={(e) => setSchedule({ ...schedule, units: Number(e.target.value) })}
                    />
                  </label>
                  <label>
                    Formule
                    <select
                      value={schedule.plan}
                      onChange={(e) => setSchedule({ ...schedule, plan: e.target.value })}
                    >
                      {catalog.definition.plans
                        .filter((p: any) => p.available || p.id === schedule.plan)
                        .map((p: any) => (
                          <option key={p.id} value={p.id}>
                            {p.name ?? p.id}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label>
                    Cycle
                    <select
                      value={schedule.cadence}
                      onChange={(e) => setSchedule({ ...schedule, cadence: e.target.value })}
                    >
                      <option value="monthly">{t('Mensuel')}</option>
                      <option value="yearly">{t('Annuel')}</option>
                    </select>
                  </label>
                  <p>
                    Au renouvellement :{' '}
                    {money(
                      (catalog.definition.plans.find((p: any) => p.id === schedule.plan)?.[
                        schedule.cadence
                      ] ?? 0) *
                        schedule.units +
                        catalog.definition.extensions.reduce(
                          (n: number, e: any) =>
                            n + (e[schedule.cadence] ?? 0) * (schedule[e.id] ?? 0),
                          0,
                        ),
                    )}{' '}
                    HT / {schedule.cadence === 'yearly' ? 'an' : 'mois'} ; pool de{' '}
                    {formatStorageBytes(
                      (catalog.definition.plans.find((p: any) => p.id === schedule.plan)
                        ?.bytesPerUnit ?? 0) *
                        schedule.units +
                        catalog.definition.extensions.reduce(
                          (n: number, e: any) => n + e.bytes * (schedule[e.id] ?? 0),
                          0,
                        ),
                    )}
                    .{' '}
                    {t(
                      'Les fonctions suivent la formule choisie ; Pro reste fermée tant que sa qualification est en cours.',
                    )}
                  </p>
                  <button className="primary" disabled={busy}>
                    {t('Programmer à l’échéance')}
                  </button>
                </form>
              )}
              <p>
                <button
                  disabled={busy}
                  onClick={() =>
                    void act(async () => {
                      await send(path + '/schedule', {
                        revision: data.account.revision,
                        cancel: !data.account.cancel_at_end,
                      });
                    })
                  }
                >
                  {data.account.cancel_at_end
                    ? t('Maintenir le renouvellement')
                    : t('Résilier à la fin de la période payée')}
                </button>
                {data.account.scheduled && (
                  <button
                    disabled={busy}
                    onClick={() =>
                      void act(async () => {
                        await send(path + '/schedule', {
                          revision: data.account.revision,
                          cancel: false,
                        });
                      })
                    }
                  >
                    {t('Annuler le changement programmé')}
                  </button>
                )}
              </p>
            </details>
          )}
          {data.adjustments.length > 0 && (
            <div>
              <p>
                {t('Régularisations à facturer : {amount} HT.', {
                  amount: money(
                    data.adjustments.reduce((n: number, r: any) => n + r.line.amountHT, 0),
                  ),
                })}
              </p>
              {organizationId && (
                <button
                  disabled={busy}
                  onClick={() =>
                    void act(async () => {
                      await send(path + '/adjustments', {});
                    })
                  }
                >
                  {t('Émettre un complément consolidé')}
                </button>
              )}
            </div>
          )}
          <table className="billing-table">
            <thead>
              <tr>
                <th>{t('Facture')}</th>
                <th>{t('Échéance')}</th>
                <th>{t('Total')}</th>
                <th>{t('Solde')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((i: any) => (
                <tr key={i.id}>
                  <td>
                    VM-{String(i.number).padStart(8, '0')}
                    {i.voided ? ' — ' + t('annulée') : ''}
                  </td>
                  <td>{date(i.due_at)}</td>
                  <td>{money(i.total)}</td>
                  <td>{money(i.remaining)}</td>
                  <td>
                    <button
                      onClick={() => {
                        setSelected(i);
                        setPayment(undefined);
                      }}
                    >
                      {t('Consulter')}
                    </button>
                    {organizationId &&
                      i.source_key.startsWith('prepaid:') &&
                      !i.voided &&
                      Number(i.remaining) === Number(i.total) && (
                        <button
                          disabled={busy}
                          onClick={() =>
                            void act(async () => {
                              await send(path + '/void-prepayment', {
                                invoiceId: i.id,
                                reason: t('Commande annulée avant paiement et activation'),
                              });
                            })
                          }
                        >
                          {t('Annuler le complément')}
                        </button>
                      )}
                    {organizationId && Number(i.remaining) > 0 && (
                      <button
                        onClick={() => {
                          setPayment({
                            invoiceId: i.id,
                            amount: Number(i.remaining),
                            key: crypto.randomUUID(),
                          });
                          setReference('');
                        }}
                      >
                        {t('Enregistrer un règlement vérifié')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {payment && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void act(async () => {
                  await send(
                    path + '/payments',
                    { invoiceId: payment.invoiceId, amount: payment.amount, method, reference },
                    'POST',
                    { 'Idempotency-Key': payment.key },
                  );
                  setPayment(undefined);
                });
              }}
            >
              <h4>{t('Règlement reçu et vérifié')}</h4>
              <p>
                {t(
                  'Réservé à l’opérateur après vérification du paiement. Ce formulaire n’effectue aucun débit.',
                )}
              </p>
              <label>
                {t('Montant reçu (FCFA)')}
                <input
                  type="number"
                  min={1}
                  required
                  value={payment.amount}
                  onChange={(e) => setPayment({ ...payment, amount: Number(e.target.value) })}
                />
              </label>
              <label>
                {t('Moyen')}
                <select value={method} onChange={(e) => setMethod(e.target.value)}>
                  <option value="transfer">{t('Virement')}</option>
                  <option value="mobile_money">{t('Mobile Money')}</option>
                  <option value="card">{t('Carte')}</option>
                </select>
              </label>
              <label>
                {t('Référence unique du paiement')}
                <input
                  required
                  minLength={5}
                  maxLength={200}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </label>
              <button className="primary" disabled={busy}>
                {t('Enregistrer le règlement')}
              </button>
            </form>
          )}
          {selected && (
            <article className="billing-document">
              <h2>
                {t('Vename Mail — Facture VM-{number}', {
                  number: String(selected.number).padStart(8, '0'),
                })}
              </h2>
              <p>
                {t('Émise le {created} · Échéance : {due}', {
                  created: date(selected.created_at),
                  due: date(selected.due_at),
                })}
              </p>
              {selected.voided && <strong>{t('DOCUMENT ANNULÉ — aucun règlement dû.')}</strong>}
              <h4>{t('Émetteur')}</h4>
              <pre>{selected.profile.seller}</pre>
              <h4>{t('Client')}</h4>
              <pre>{selected.profile.customer}</pre>
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>{t('Désignation')}</th>
                    <th>{t('Montant HT')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.lines.map((l: any, i: number) => (
                    <tr key={i}>
                      <td>
                        {l.label}
                        {l.remainingDays !== undefined &&
                          ' — ' + t('{days} jours', { days: l.remainingDays })}
                      </td>
                      <td>{money(l.amountHT)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>
                {t('Total HT : {net} · Taxe ({rate} %) : {tax}', {
                  net: money(selected.amount_ht),
                  rate: selected.profile.taxBasisPoints / 100,
                  tax: money(selected.tax),
                })}
              </p>
              <h3>{t('Total : {total}', { total: money(selected.total) })}</h3>
              <h4>{t('Reçus de règlement')}</h4>
              {data.payments
                .filter((p: any) => p.invoice_id === selected.id)
                .map((p: any) => (
                  <p key={p.id}>
                    {t('{date} — {amount} · {method} · Référence {reference}', {
                      date: date(p.created_at),
                      amount: money(p.amount),
                      method: p.method,
                      reference: p.reference,
                    })}
                  </p>
                ))}
              <button onClick={() => window.print()}>{t('Imprimer / enregistrer en PDF')}</button>
              <button
                onClick={() =>
                  download(
                    {
                      invoice: selected,
                      receipts: data.payments.filter((p: any) => p.invoice_id === selected.id),
                    },
                    `facture-${selected.number}.json`,
                  )
                }
              >
                {t('Exporter facture et reçus')}
              </button>
            </article>
          )}
          <details>
            <summary>{t('Notifications de facturation')}</summary>
            <ul>
              {data.notices.map((n: any) => (
                <li key={n.key}>
                  {date(n.created_at)} — {n.message}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
    </section>
  );
}
